package dev.lingvo.shared

import kotlinx.serialization.Serializable

const val RELEASE_POLICY_LOCAL_DEFAULTS = "LOCAL_DEFAULTS"
const val RELEASE_POLICY_REVIEWED_CURRENT = "REVIEWED_CURRENT"
const val RELEASE_SCOPE_LOCAL_RESOURCES = "LOCAL_RESOURCES"
const val RELEASE_SCOPE_HOSTED_PROJECT = "HOSTED_PROJECT"
const val RELEASE_SCOPE_CHECKOUT_RESOURCES = "CHECKOUT_RESOURCES"
const val MAX_RELEASE_LOGICAL_FILES = 100
const val MAX_RELEASE_LOCALES = 25
const val MAX_RELEASE_PHYSICAL_RESOURCES = 1000
const val MAX_RELEASE_RESOURCE_BYTES = 5 * 1024 * 1024
const val MAX_RELEASE_TOTAL_BYTES = 32 * 1024 * 1024
const val MAX_RELEASE_PARSED_ENTRIES = 100000
const val MAX_RELEASE_FINDINGS = 100

/** Captured UTF-8 content. Required null means the requested physical file was missing. */
@Serializable
data class ReleaseResourceDto(
    val path: String,
    val format: FileFormat,
    val content: String?,
    val pluralStyle: PluralStyle = PluralStyle.NESTED,
)

/** Paths reference the request's physical resource pool; they do not read the server filesystem. */
@Serializable
data class ReleaseLocaleResourcesDto(
    val locale: String,
    val resources: List<String>,
)

@Serializable
data class ReleaseFileResourcesDto(
    val path: String,
    val source: List<String>,
    val translations: List<ReleaseLocaleResourcesDto>,
    val fileId: String? = null,
)

/** Explicit file/locale scope. A multilingual physical catalogue is uploaded once. */
@Serializable
data class ReleaseResourcesRequest(
    val sourceLocale: String,
    val locales: List<String>,
    val files: List<ReleaseFileResourcesDto>,
    val resources: List<ReleaseResourceDto>,
) {
    init { validateReleaseResourcesRequest(this)?.let { require(false) { it } } }
}

@Serializable
data class ReleaseCheckScopeDto(
    val projectSlug: String?,
    val sourceLocale: String,
    val filePaths: List<String>,
    val locales: List<String>,
    val kind: String,
)

/** One use of captured physical bytes. Shared catalogues may appear under several locale roles. */
@Serializable
data class ReleaseResourceDigestDto(
    val logicalPath: String,
    val path: String,
    val locale: String,
    val role: String,
    val format: FileFormat,
    val sha256: String?,
    val byteCount: Int?,
)

/** Codes are extensible data, never a client's substitute for the explicit report verdict. */
@Serializable
data class ReleaseCheckFindingDto(
    val code: String,
    val message: String,
    val severity: QaSeverity,
    val filePath: String? = null,
    val resourcePath: String? = null,
    val locale: String? = null,
    val keyId: String? = null,
    val keyName: String? = null,
    val check: String? = null,
    val currentSourceView: SourceViewDto? = null,
    val targetRevision: String? = null,
)

/** Counts cover the complete declared scope, before findings are capped. */
@Serializable
data class ReleaseCheckCountsDto(
    val logicalFiles: Int,
    val physicalResources: Int,
    val sourceKeys: Int,
    val targetStrings: Int,
    val excludedKeys: Int,
    val findings: Int,
    val errors: Int,
    val warnings: Int,
    val byCode: Map<String, Int>,
)

/** Point-in-time evaluation, not a persistent authorization for a future build or download. */
@Serializable
data class ReleaseCheckReportDto(
    val scope: ReleaseCheckScopeDto,
    val policy: String,
    val complete: Boolean,
    val passed: Boolean,
    val evaluatedAt: String,
    val counts: ReleaseCheckCountsDto,
    val findings: List<ReleaseCheckFindingDto>,
    val digests: List<ReleaseResourceDigestDto>,
    val truncated: Boolean,
) {
    init { validateReleaseCheckReport(this)?.let { require(false) { it } } }
}
